import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ReviewCard } from '@/components/reviews/ReviewCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { reviewsService } from '@/services/reviews/reviewsService';
import { cn } from '@/lib/utils';
import type { Review, ReviewsSummary } from '@/types/reviews';

const MAX_REVIEWS = 12;
const AUTOPLAY_DELAY_MS = 6000;

/**
 * Home first-impression social proof section — carousel.
 * Data source: live Konfidency reviews via reviewsService (same source as PDP
 * and the /avaliacoes hub). No invented reviews — if the source returns nothing,
 * the section renders nothing.
 * Autoplay pauses on hover/focus and is disabled entirely under
 * prefers-reduced-motion (manual navigation only).
 */
export const HomeReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [summary, setSummary] = useState<ReviewsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const autoplay = useRef(
    Autoplay({
      delay: AUTOPLAY_DELAY_MS,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
    })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    reducedMotion ? [] : [autoplay.current]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  useEffect(() => {
    let active = true;
    Promise.all([
      reviewsService.getHomeCarouselReviews().catch(() => [] as Review[]),
      reviewsService.getReviewsSummary().catch(() => null),
    ]).then(([carousel, sum]) => {
      if (!active) return;
      // Prefer strong reviews (rating 5), already sorted by service; cap at MAX_REVIEWS
      const top = carousel.filter(r => r.rating >= 5).slice(0, MAX_REVIEWS);
      setReviews(top.length >= 3 ? top : carousel.slice(0, MAX_REVIEWS));
      setSummary(sum);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  if (!loading && reviews.length === 0) return null;

  const avg = summary ? summary.averageRating.toFixed(1) : '4.8';
  const count = summary?.totalReviews;

  return (
    <section className="bg-background py-16 md:py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="font-narrow text-3xl md:text-4xl font-light text-brand-deep">
            O que dizem nossos clientes
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex gap-1" aria-label={`${avg} de 5 estrelas`}>
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className="h-5 w-5 fill-brand-amber text-brand-amber" />
              ))}
            </div>
            <span className="text-base text-foreground/70">
              <strong className="text-brand-deep">{avg}/5</strong>
              {count ? ` em ${count} avaliações verificadas` : ' em avaliações verificadas'}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 space-y-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="relative">
            <div
              className="overflow-hidden"
              ref={emblaRef}
              aria-roledescription="carrossel"
              aria-label="Avaliações de clientes"
            >
              <div className="flex gap-6">
                {reviews.map((review, i) => (
                  <div
                    key={review.id}
                    className="flex-none w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] min-w-0"
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`Avaliação ${i + 1} de ${reviews.length}`}
                  >
                    <ReviewCard review={review} />
                  </div>
                ))}
              </div>
            </div>

            {/* Manual navigation: arrows + dots */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={scrollPrev}
                aria-label="Avaliação anterior"
                className="rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <div className="flex items-center gap-2" role="tablist" aria-label="Escolher avaliação">
                {reviews.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === selectedIndex}
                    aria-label={`Ir para avaliação ${i + 1}`}
                    onClick={() => scrollTo(i)}
                    className={cn(
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      i === selectedIndex ? 'bg-primary' : 'bg-border hover:bg-primary/50'
                    )}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={scrollNext}
                aria-label="Próxima avaliação"
                className="rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/avaliacoes"
            className="inline-flex items-center gap-2 text-primary hover:text-brand-deep font-medium transition-colors"
          >
            Ver todas as avaliações
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeReviews;
